import { useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { ApiResult } from '@/api/client'
import type { components } from '@/api/schema'
import { useMutation, useQuery } from '@/api/query'
import { downloadCsv, toCsv } from '@/lib/csv'
import { formatDate } from '@/lib/format'
import { Button } from '@/ui/Button'
import { Modal } from '@/ui/Modal'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type TrackedKeyword = components['schemas']['TrackedKeywordOut']
type Competitor = components['schemas']['CompetitorOut']
type Gap = components['schemas']['GapOut']
type Run = components['schemas']['RunOut']

type Tab = 'tracked' | 'gap' | 'competitors'
type SortKey = 'keyword' | 'position' | 'change' | 'clicks' | 'volume' | 'difficulty'
type Direction = 'ascending' | 'descending'

const IST = 'Asia/Kolkata'

/** Rows per page in the *Tracked* table. S-D5 draws six, which is a mockup with
 *  seven rows in it; a tenant on a niche pack has ninety-two. */
const PAGE_SIZE = 25

/** How many gap keywords **Add top N to tracking** takes. S-D5 says ten. */
const BULK_LIMIT = 10

/**
 * The sortable columns, and which way each one should go on its *first* press.
 *
 * The direction is per column rather than "ascending, then descending
 * everywhere", because the useful end of each of these is a different end:
 * position 1 is the good one, the biggest gain in *Change* is the good one, and
 * the easiest *Difficulty* is the low one. A table where the first click on
 * **Change** shows a tenant their worst four weeks is technically consistent
 * and practically wrong.
 *
 * S-D5 marks Keyword, Pos, Change and Volume sortable and leaves Clicks (which
 * it does not draw) and Difficulty plain. All six sort here: they sit next to
 * each other in one row of numbers, and two that refuse to sort between four
 * that do reads as a bug rather than as a decision.
 */
const COLUMNS: {
  key: SortKey
  label: string
  numeric: boolean
  first: Direction
  value: (row: TrackedKeyword) => string | number | null
}[] = [
  {
    key: 'keyword',
    label: 'Keyword',
    numeric: false,
    first: 'ascending',
    value: (row) => row.keyword,
  },
  {
    key: 'position',
    label: 'Position',
    numeric: true,
    first: 'ascending',
    value: (row) => row.position ?? null,
  },
  {
    key: 'change',
    label: 'Change',
    numeric: true,
    first: 'descending',
    value: (row) => row.change ?? null,
  },
  {
    key: 'clicks',
    label: 'Clicks',
    numeric: true,
    first: 'descending',
    value: (row) => row.clicks,
  },
  {
    key: 'volume',
    label: 'Volume',
    numeric: true,
    first: 'descending',
    value: (row) => row.volume ?? null,
  },
  {
    key: 'difficulty',
    label: 'Difficulty',
    numeric: true,
    first: 'ascending',
    value: (row) => row.difficulty ?? null,
  },
]

/** The export's columns. Wider than the screen's, because a column costs a
 *  character in a file and a scrollbar on a table. */
const EXPORT_HEADER = [
  'Keyword',
  'Position',
  'Change (places gained)',
  'Clicks',
  'Impressions',
  'Volume',
  'Difficulty',
  'Ranking page',
  'Captured on',
]

/**
 * One column's comparison, with blanks pinned to the bottom either way.
 *
 * A null is not a small number: most rows have no volume and no difficulty at
 * all (COGS §8), so sorting them as zero would fill the first page with the
 * keywords the tenant knows least about — which is `db/seo_rank.py`'s
 * `NULLS LAST` argument, applied to the same data one layer up.
 */
function compare(
  a: string | number | null,
  b: string | number | null,
  direction: Direction,
): number {
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  const order =
    typeof a === 'string' && typeof b === 'string' ? a.localeCompare(b) : Number(a) - Number(b)
  return direction === 'ascending' ? order : -order
}

/**
 * Keywords — S-D5's `seo#keywords`, on real data (S-B24).
 *
 * Three tabs, and which of them costs money is the thing this screen has to be
 * honest about. **Tracked** and the rank movement behind it are free: Search
 * Console gives a tenant their own property's figures away, and COGS §8's rule
 * is that every question about a tenant's own site is answered there. **Competitor
 * gap** and **Competitors** are bought — two DataForSEO calls a refresh — so the
 * button says the price before it is pressed and the tab renders whatever the
 * last refresh bought rather than fetching a fresh one on load. A screen that
 * silently billed a tenant for opening it is the failure this shape exists to
 * prevent.
 *
 * **Nothing here computes a rank change.** `change` comes off the API already
 * inverted, because "position 6 beats position 28" is a rule the server states
 * once and a second implementation in TypeScript would be the copy that got the
 * sign backwards on the one screen where green and red mean money.
 *
 * The cannibalization screen is a sub-route rather than a fourth tab: a
 * cross-site conflict belongs to two sites at once and the tab strip here is
 * per-site, so it would be a tab that contradicted the selector above it.
 */
export function Keywords(): JSX.Element {
  const toast = useToast()
  const [siteId, setSiteId] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('tracked')

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])
  const listed = sites.state.data?.sites ?? []
  const selectedId = siteId ?? listed[0]?.id ?? null
  const site = listed.find((row) => row.id === selectedId) ?? null

  const keywords = useQuery(
    (signal) =>
      selectedId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/sites/{site_id}/keywords', {
              params: { path: { site_id: selectedId } },
              signal,
            }),
          ),
    [selectedId],
  )

  const competitors = useQuery(
    (signal) =>
      selectedId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/sites/{site_id}/competitors', {
              params: { path: { site_id: selectedId } },
              signal,
            }),
          ),
    [selectedId],
  )

  const refreshRanks = useMutation((id: string) =>
    call(() =>
      api.POST('/api/v1/seo/sites/{site_id}/rank-runs', { params: { path: { site_id: id } } }),
    ),
  )

  const buy = useMutation((id: string) =>
    call(() =>
      api.POST('/api/v1/seo/sites/{site_id}/competitors/refresh', {
        params: { path: { site_id: id } },
      }),
    ),
  )

  const track = useMutation((input: { siteId: string; gapId: string }) =>
    call(() =>
      api.POST('/api/v1/seo/sites/{site_id}/keywords', {
        params: { path: { site_id: input.siteId } },
        body: { gap_id: input.gapId },
      }),
    ),
  )

  /**
   * **Add top 10 to tracking** — S-D5's bulk action, as ten calls to the route
   * that already exists rather than a new one.
   *
   * Sequential, not `Promise.all`. Each press opens its own tenant transaction
   * and writes its own audit row, so ten at once is a burst against one
   * connection pool for no gain — and a failure part-way through a parallel
   * batch leaves a set of keywords nobody can reason about. In order, the first
   * refusal stops it and everything before it has landed, which is a state the
   * screen can describe.
   */
  const trackTop = useMutation(
    async (input: {
      siteId: string
      gapIds: string[]
    }): Promise<ApiResult<{ tracked: number }>> => {
      let tracked = 0
      for (const gapId of input.gapIds) {
        const result = await call(() =>
          api.POST('/api/v1/seo/sites/{site_id}/keywords', {
            params: { path: { site_id: input.siteId } },
            body: { gap_id: gapId },
          }),
        )
        if (!result.ok) return { ok: false, failure: result.failure }
        tracked += 1
      }
      return { ok: true, data: { tracked } }
    },
  )

  const data = keywords.state.data
  const running = data?.running ?? false
  const market = competitors.state.data

  return (
    <>
      <div className="ar-row">
        <h1 className="ar-h1">Keywords</h1>
        <span className="app-spacer" />
        {listed.length > 1 ? (
          <select
            className="ar-select"
            aria-label="Which site"
            value={selectedId ?? ''}
            onChange={(event) => {
              setSiteId(event.target.value)
            }}
          >
            {listed.map((row) => (
              <option key={row.id} value={row.id}>
                {row.domain}
              </option>
            ))}
          </select>
        ) : null}
        {site !== null ? (
          <Button
            disabled={running || refreshRanks.pending}
            onClick={() => {
              void refreshRanks.run(site.id).then((result) => {
                if (!result.ok) return
                keywords.refetch()
                toast.show({
                  kind: 'success',
                  title: result.data.queued
                    ? 'Refreshing rankings'
                    : 'A refresh is already running',
                  body: `AugRank is reading Search Console for ${site.domain} and checking whether any of your pages are competing with each other.`,
                })
              })
            }}
          >
            {running ? 'Refreshing…' : 'Refresh rankings'}
          </Button>
        ) : null}
      </div>

      {refreshRanks.state.status === 'error' ? (
        <InlineError failure={refreshRanks.state.failure} />
      ) : null}
      {buy.state.status === 'error' ? <InlineError failure={buy.state.failure} /> : null}
      {track.state.status === 'error' ? <InlineError failure={track.state.failure} /> : null}
      {trackTop.state.status === 'error' ? <InlineError failure={trackTop.state.failure} /> : null}

      {sites.state.status === 'loading' ? (
        <Loading label="Loading your sites" />
      ) : sites.state.status === 'error' ? (
        <ErrorState failure={sites.state.failure} onRetry={sites.refetch} />
      ) : site === null ? (
        <Empty
          title="No site yet"
          body="Add your website and AugRank can start tracking where it ranks."
          action={<Link to="/onboarding/site">Add your site</Link>}
        />
      ) : (
        <div className="ar-stack">
          <RunNote run={data?.run ?? null} />
          <div className="ar-tabs" role="tablist" aria-label="Keyword views">
            <TabButton
              id="tracked"
              label="Tracked"
              count={data?.keywords.length ?? 0}
              active={tab}
              onSelect={setTab}
            />
            <TabButton
              id="gap"
              label="Competitor gap"
              count={market?.gaps.length ?? 0}
              active={tab}
              onSelect={setTab}
            />
            <TabButton
              id="competitors"
              label="Competitors"
              count={market?.competitors.length ?? 0}
              active={tab}
              onSelect={setTab}
            />
            <span className="ar-card__spacer" />
            <Link className="ar-link" to="/seo/keywords/cannibalization">
              Pages competing with each other
            </Link>
          </div>

          {tab === 'tracked' ? (
            keywords.state.status === 'loading' ? (
              <Loading label="Loading your keywords" />
            ) : keywords.state.status === 'error' ? (
              <ErrorState failure={keywords.state.failure} onRetry={keywords.refetch} />
            ) : (
              <TrackedTable
                keywords={data?.keywords ?? []}
                domain={site.domain}
                siteId={site.id}
                onUntracked={() => {
                  keywords.refetch()
                  // The gap table offers *Track* for terms this site does not
                  // have, so a keyword that just left the tracked set belongs
                  // back in it.
                  competitors.refetch()
                }}
              />
            )
          ) : competitors.state.status === 'loading' ? (
            <Loading label="Loading competitor data" />
          ) : competitors.state.status === 'error' ? (
            <ErrorState failure={competitors.state.failure} onRetry={competitors.refetch} />
          ) : (market?.competitors.length ?? 0) === 0 ? (
            <BuyPrompt
              domain={site.domain}
              pending={buy.pending}
              onBuy={() => {
                void buy.run(site.id).then((result) => {
                  if (!result.ok) return
                  competitors.refetch()
                  toast.show({
                    kind: 'success',
                    title: `Found ${String(result.data.competitors.length)} competitor(s)`,
                    body: `${String(result.data.vendor_calls)} paid lookup(s) were charged to your budget.`,
                  })
                })
              }}
            />
          ) : tab === 'gap' ? (
            <GapTable
              gaps={market?.gaps ?? []}
              pending={track.pending || trackTop.pending}
              bulkPending={trackTop.pending}
              onTrackTop={(gapIds) => {
                void trackTop.run({ siteId: site.id, gapIds }).then((result) => {
                  // Refetched either way: a batch that stopped half-way still
                  // tracked everything before the refusal, and those rows have
                  // to stop offering a Track button. The InlineError above says
                  // why it stopped; the table says how far it got.
                  competitors.refetch()
                  keywords.refetch()
                  if (!result.ok) return
                  toast.show({
                    kind: 'success',
                    title: `Now tracking ${String(result.data.tracked)} more keyword(s)`,
                    body: 'Their positions will appear after the next refresh.',
                  })
                })
              }}
              onTrack={(gapId) => {
                void track.run({ siteId: site.id, gapId }).then((result) => {
                  if (!result.ok) return
                  competitors.refetch()
                  keywords.refetch()
                  toast.show({
                    kind: 'success',
                    title: `Now tracking “${result.data.keyword}”`,
                    body: 'Its position will appear after the next refresh.',
                  })
                })
              }}
            />
          ) : (
            <CompetitorCards competitors={market?.competitors ?? []} />
          )}
        </div>
      )}
    </>
  )
}

function TabButton({
  id,
  label,
  count,
  active,
  onSelect,
}: {
  id: Tab
  label: string
  count: number
  active: Tab
  onSelect: (tab: Tab) => void
}): JSX.Element {
  return (
    <button
      type="button"
      className="ar-tab"
      role="tab"
      aria-selected={active === id}
      onClick={() => {
        onSelect(id)
      }}
    >
      {label}
      <span className="ar-tab__count">{count}</span>
    </button>
  )
}

/**
 * What the last run did, and — when it degraded — what it could not do.
 *
 * A `partial` run is the one that needs saying out loud, for the reason the
 * audit screen gives about its own: a run that could not reach Search Console
 * produced no positions at all, and rendering that identically to a clean run
 * would show yesterday's figures as though they were today's.
 */
function RunNote({ run }: { run: Run | null }): JSX.Element | null {
  if (run === null) {
    return (
      <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
        AugRank has not read your rankings yet. Press <strong>Refresh rankings</strong> — it uses
        Search Console, which is free for a property you have verified.
      </p>
    )
  }
  const summary = (run.summary ?? {}) as Record<string, unknown>
  const degraded = Array.isArray(summary.degraded) ? (summary.degraded as unknown[]) : []
  return (
    <div className="ar-row ar-text-body-sm ar-muted" style={{ gap: 'var(--ar-space-3)' }}>
      {run.status === 'queued' || run.status === 'running' ? (
        <span className="ar-badge">Refreshing</span>
      ) : run.status === 'partial' ? (
        <span className="ar-badge ar-badge--warning">Partly measured</span>
      ) : null}
      <span>
        {run.finished_at !== null && run.finished_at !== undefined
          ? `Last refreshed ${formatDate(run.finished_at)}`
          : 'Refresh in progress'}
      </span>
      {degraded.length > 0 ? <span>· {degraded.map(String).join(' ')}</span> : null}
    </div>
  )
}

/**
 * The *Tracked* table: position, movement, difficulty, and which page is ranking.
 *
 * **Volume and difficulty are blank for most rows and that is correct.** COGS §8
 * forbids buying keyword data about a tenant's own site — Search Console is free
 * and measures rather than models — so a figure appears only where the keyword
 * came from a competitor-gap row that was already paid for.
 *
 * **Filtering, sorting and paging all happen here rather than in the API, and
 * that is a property of the endpoint rather than a shortcut.**
 * `GET …/keywords` answers with the whole tracked set in one response — it has
 * no `?q=`, no `?sort=` and no cursor — so the rows are already in the browser
 * by the time any of these controls exist. Asking the server to re-sort a list
 * it has already sent would be a round trip to reorder an array. If the set
 * ever outgrows one response, the paging here is the thing that has to move,
 * and the note above `PAGE_SIZE` is where that argument continues.
 */
function TrackedTable({
  keywords,
  domain,
  siteId,
  onUntracked,
}: {
  keywords: TrackedKeyword[]
  domain: string
  siteId: string
  onUntracked: () => void
}): JSX.Element {
  const [filter, setFilter] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; direction: Direction } | null>(null)
  const [page, setPage] = useState(1)
  const [confirming, setConfirming] = useState<TrackedKeyword | null>(null)

  const untrack = useMutation((input: { siteId: string; keywordId: string }) =>
    call(() =>
      api.DELETE('/api/v1/seo/sites/{site_id}/keywords/{keyword_id}', {
        params: { path: { site_id: input.siteId, keyword_id: input.keywordId } },
      }),
    ),
  )

  if (keywords.length === 0) {
    return (
      <Empty
        title="No keywords tracked yet"
        body={`Choose the terms you want ${domain} to rank for and AugRank will record where you sit for each of them, every day.`}
        action={<Link to="/onboarding/keywords">Choose keywords</Link>}
      />
    )
  }

  const needle = filter.trim().toLowerCase()
  const matched =
    needle === '' ? keywords : keywords.filter((row) => row.keyword.toLowerCase().includes(needle))
  const column = sort === null ? null : (COLUMNS.find((entry) => entry.key === sort.key) ?? null)
  const ordered =
    sort === null || column === null
      ? matched
      : [...matched].sort((a, b) => compare(column.value(a), column.value(b), sort.direction))

  // The page is clamped at render rather than corrected in an effect: a filter
  // that narrows ninety rows to three while the tenant is on page four would
  // otherwise paint one empty table before the effect ran.
  const pages = Math.max(1, Math.ceil(ordered.length / PAGE_SIZE))
  const current = Math.min(page, pages)
  const start = (current - 1) * PAGE_SIZE
  const visible = ordered.slice(start, start + PAGE_SIZE)

  const sortBy = (key: SortKey, first: Direction): void => {
    setPage(1)
    setSort((previous) =>
      previous === null || previous.key !== key
        ? { key, direction: first }
        : { key, direction: previous.direction === 'ascending' ? 'descending' : 'ascending' },
    )
  }

  /**
   * The export is what the tenant is *looking at* — filtered and sorted — but
   * every page of it, not the twenty-five on screen. Pagination is a property
   * of the table rather than of the answer, and a file that stopped at row 25
   * because of where somebody happened to be standing would be the kind of
   * quiet truncation nobody notices until it matters.
   */
  const exportCsv = (): void => {
    const today = new Date().toLocaleDateString('en-CA', { timeZone: IST })
    downloadCsv(
      `keywords-${domain}-${today}.csv`,
      toCsv(
        EXPORT_HEADER,
        ordered.map((row) => [
          row.keyword,
          row.position ?? null,
          row.change ?? null,
          row.clicks,
          row.impressions,
          row.volume ?? null,
          row.difficulty ?? null,
          // The whole URL rather than `pathOf`. On screen the path is enough
          // because every row is the same site; a file gets mailed to somebody
          // who does not have that context.
          row.ranking_page ?? null,
          row.captured_on ?? null,
        ]),
      ),
    )
  }

  return (
    <section className="ar-card">
      <div className="ar-table-toolbar">
        <input
          className="ar-input"
          type="search"
          value={filter}
          aria-label="Filter keywords"
          placeholder="Filter keywords…"
          onChange={(event) => {
            setFilter(event.target.value)
            setPage(1)
          }}
        />
        <span className="ar-card__spacer" />
        {/* Always mounted, so it is a live region rather than an element that
            appears — an `aria-live` node added to the DOM after the change it
            describes announces nothing. */}
        <span className="ar-text-body-sm ar-muted" aria-live="polite">
          {needle === ''
            ? ''
            : `${ordered.length.toLocaleString('en-IN')} of ${keywords.length.toLocaleString('en-IN')} match`}
        </span>
        <Button tone="ghost" size="sm" disabled={ordered.length === 0} onClick={exportCsv}>
          Export CSV
        </Button>
      </div>

      {ordered.length === 0 ? (
        <Empty
          icon="⌕"
          title="No keywords match"
          body={`Nothing you are tracking contains “${filter.trim()}”. Clear the box to see all ${keywords.length.toLocaleString('en-IN')} again.`}
        />
      ) : (
        <>
          <div className="ar-table-wrap">
            <table className="ar-table">
              <thead>
                <tr>
                  {COLUMNS.map((entry) => {
                    const state: Direction | 'none' =
                      sort !== null && sort.key === entry.key ? sort.direction : 'none'
                    return (
                      // `aria-sort` belongs on the header cell — that is where a
                      // screen reader reads it from. It is repeated on the
                      // button because the Atlas layer keys the arrow's colour
                      // off `.ar-sort[aria-sort=…]`, and on a `role="button"`
                      // the attribute is ignored rather than misreported.
                      <th
                        key={entry.key}
                        className={entry.numeric ? 'ar-num' : undefined}
                        aria-sort={state}
                      >
                        <button
                          type="button"
                          className="ar-sort"
                          aria-sort={state}
                          onClick={() => {
                            sortBy(entry.key, entry.first)
                          }}
                        >
                          {entry.label}
                          <span className="ar-sort__arrow" aria-hidden="true">
                            {state === 'none' ? '↕' : state === 'ascending' ? '↑' : '↓'}
                          </span>
                        </button>
                      </th>
                    )
                  })}
                  <th>Ranking page</th>
                  {/* No label: the column holds one control per row and a
                      heading over it would be read out on every cell. */}
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr key={row.id}>
                    <td>{row.keyword}</td>
                    <td className="ar-num">
                      {row.position === null || row.position === undefined
                        ? '—'
                        : row.position.toFixed(1)}
                    </td>
                    <td className="ar-num">
                      <Change value={row.change ?? null} />
                    </td>
                    <td className="ar-num">{row.clicks.toLocaleString('en-IN')}</td>
                    <td className="ar-num">
                      {row.volume === null || row.volume === undefined
                        ? '—'
                        : row.volume.toLocaleString('en-IN')}
                    </td>
                    <td className="ar-num">
                      {row.difficulty === null || row.difficulty === undefined
                        ? '—'
                        : row.difficulty.toLocaleString('en-IN')}
                    </td>
                    <td className="ar-mono" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
                      {row.ranking_page === null || row.ranking_page === undefined
                        ? '—'
                        : pathOf(row.ranking_page)}
                    </td>
                    <td className="ar-num">
                      {/*
                        The other half of *Track*, and it asks first. Untracking
                        frees a slot in the plan's keyword allowance and stops
                        the nightly measurement, so it is not an undo — the
                        positions already recorded survive (the server disables
                        the row rather than deleting it, because `rank_history`
                        cascades) but the days between now and re-tracking are
                        gone for good and no chart can show what was not measured.
                      */}
                      <Button
                        tone="ghost"
                        size="sm"
                        onClick={() => {
                          setConfirming(row)
                        }}
                      >
                        Stop tracking
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {confirming !== null ? (
            <Modal
              title={`Stop tracking “${confirming.keyword}”?`}
              onClose={() => {
                setConfirming(null)
              }}
              footer={
                <>
                  <Button
                    tone="ghost"
                    onClick={() => {
                      setConfirming(null)
                    }}
                  >
                    Keep tracking it
                  </Button>
                  <Button
                    tone="danger"
                    busy={untrack.pending}
                    busyLabel="Stopping"
                    onClick={() => {
                      void (async () => {
                        const result = await untrack.run({
                          siteId,
                          keywordId: confirming.id,
                        })
                        if (!result.ok) return
                        setConfirming(null)
                        onUntracked()
                      })()
                    }}
                  >
                    Stop tracking
                  </Button>
                </>
              }
            >
              <InlineError failure={untrack.state.failure} />
              <p style={{ marginTop: 0 }}>
                AugRank will stop measuring where {domain} ranks for this term tonight, and the slot
                goes back to your plan&rsquo;s keyword allowance.
              </p>
              <p>
                The positions already recorded are <strong>kept</strong>, so tracking it again later
                picks the chart back up rather than starting from zero. The days in between are not
                measured and cannot be filled in afterwards.
              </p>
            </Modal>
          ) : null}

          {ordered.length > PAGE_SIZE ? (
            <div className="ar-pagination">
              <span className="ar-pagination__info" aria-live="polite">
                Showing {(start + 1).toLocaleString('en-IN')}–
                {(start + visible.length).toLocaleString('en-IN')} of{' '}
                {ordered.length.toLocaleString('en-IN')}
              </span>
              <Button
                tone="ghost"
                size="sm"
                disabled={current === 1}
                onClick={() => {
                  setPage(current - 1)
                }}
              >
                Previous
              </Button>
              <Button
                tone="ghost"
                size="sm"
                disabled={current === pages}
                onClick={() => {
                  setPage(current + 1)
                }}
              >
                Next
              </Button>
            </div>
          ) : null}
        </>
      )}

      <div className="ar-card__footer">
        <span className="ar-text-body-sm ar-muted">
          Positions are the average Search Console reports over the last 28 days, ending three days
          back — Google&rsquo;s most recent days are incomplete and would change under you.
        </span>
      </div>
    </section>
  )
}

/**
 * Places gained, with the sign the tenant reads rather than the one the data has.
 *
 * A positive `change` is an improvement, because the server already inverted it.
 * `aria-label` spells it out: colour is the only other thing carrying the
 * direction, and colour is not available to everybody.
 */
function Change({ value }: { value: number | null }): JSX.Element {
  if (value === null || value === 0) return <span className="ar-muted">—</span>
  const better = value > 0
  return (
    <span
      className={`ar-badge ar-badge--${better ? 'success' : 'danger'}`}
      aria-label={`${Math.abs(value).toFixed(0)} places ${better ? 'better' : 'worse'}`}
    >
      {better ? '▲' : '▼'} {Math.abs(value).toFixed(0)}
    </span>
  )
}

/**
 * The one place in the SEO module that asks a tenant to spend money.
 *
 * The price is stated before the press rather than after it. COGS §3.1 prices a
 * competitor lookup and a gap lookup; what a tenant sees is that this is *paid*
 * and *charged to their budget*, because the rupee figure moves with the
 * exchange rate and a stale number on a button is worse than none.
 */
function BuyPrompt({
  domain,
  pending,
  onBuy,
}: {
  domain: string
  pending: boolean
  onBuy: () => void
}): JSX.Element {
  return (
    <Empty
      title="Find out who you are competing with"
      body={`AugRank will ask a paid data service which domains rank for the same terms as ${domain}, and which of their keywords you do not have. It makes two paid lookups and charges them to your budget — everything else on this screen is free.`}
      action={
        <Button disabled={pending} onClick={onBuy}>
          {pending ? 'Looking…' : 'Find competitors — 2 paid lookups'}
        </Button>
      }
    />
  )
}

/**
 * *They rank, you don&rsquo;t* — and the buttons that start tracking them.
 *
 * **What "top" means is the order the rows are already in.**
 * `db/seo_rank.py` returns gaps by monthly searches, most first, with unknown
 * volume last — so the bulk button takes the first ten untracked rows and that
 * is the same ten the tenant is reading down. It is not a second opinion about
 * which keywords are worth having.
 *
 * The button&rsquo;s label counts what it will actually do rather than always
 * saying ten: with four gaps left it says four, because a button that promises
 * ten and adds four has lied about the only thing it said.
 */
function GapTable({
  gaps,
  pending,
  bulkPending,
  onTrack,
  onTrackTop,
}: {
  gaps: Gap[]
  pending: boolean
  bulkPending: boolean
  onTrack: (gapId: string) => void
  onTrackTop: (gapIds: string[]) => void
}): JSX.Element {
  if (gaps.length === 0) {
    return (
      <Empty
        title="No gap found"
        body="The competitor we looked at does not rank for anything you are missing — which is a good sign rather than an error."
      />
    )
  }
  const top = gaps.filter((gap) => !gap.tracked).slice(0, BULK_LIMIT)
  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h3>They rank, you don&rsquo;t</h3>
        <span className="ar-card__spacer" />
        {top.length > 0 ? (
          <Button
            tone="primary"
            size="sm"
            disabled={pending}
            busy={bulkPending}
            busyLabel="Adding keywords"
            onClick={() => {
              onTrackTop(top.map((gap) => gap.id))
            }}
          >
            Add top {top.length} to tracking
          </Button>
        ) : null}
      </div>
      <p
        className="ar-text-body-sm ar-muted"
        style={{ margin: 'var(--ar-space-3) var(--ar-space-5) 0' }}
      >
        Ordered by monthly searches, most first — tracking one is free, because a position is
        something Search Console measures rather than something we buy.
      </p>
      <div className="ar-table-wrap">
        <table className="ar-table">
          <thead>
            <tr>
              <th>Keyword</th>
              <th className="ar-num">Volume</th>
              <th>Who ranks</th>
              <th className="ar-num">Their position</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {gaps.map((gap) => (
              <tr key={gap.id}>
                <td>{gap.keyword}</td>
                <td className="ar-num">
                  {gap.volume === null || gap.volume === undefined
                    ? '—'
                    : gap.volume.toLocaleString('en-IN')}
                </td>
                <td className="ar-muted">{gap.competitor_domain}</td>
                <td className="ar-num">
                  {gap.competitor_position === null || gap.competitor_position === undefined
                    ? '—'
                    : gap.competitor_position}
                </td>
                <td className="ar-num">
                  {gap.tracked ? (
                    <span className="ar-badge ar-badge--success">Tracked</span>
                  ) : (
                    <Button
                      tone="ghost"
                      disabled={pending}
                      onClick={() => {
                        onTrack(gap.id)
                      }}
                    >
                      Track
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

/**
 * The competitor set, strongest overlap first.
 *
 * The meter is the share of *this set&rsquo;s* strongest overlap rather than a
 * percentage of anything real — S-D5 draws one, and a bar that implied a
 * measurement AugRank does not have would be worse than no bar. The numbers
 * beside it are the measurement.
 */
function CompetitorCards({ competitors }: { competitors: Competitor[] }): JSX.Element {
  const strongest = Math.max(1, ...competitors.map((row) => row.intersections))
  return (
    <div className="ar-grid ar-grid--2">
      {competitors.map((row) => (
        <section className="ar-card" key={row.id}>
          <div className="ar-card__body">
            <strong>{row.domain}</strong>
            <p className="ar-text-body-sm ar-muted" style={{ margin: 'var(--ar-space-2) 0' }}>
              Overlaps on {row.intersections.toLocaleString('en-IN')} of your keywords
              {row.gap_keywords > 0
                ? ` · ranks for ${row.gap_keywords.toLocaleString('en-IN')} you don’t`
                : ''}
            </p>
            <div className="ar-meter">
              <div
                className="ar-meter__fill"
                style={{ width: `${((row.intersections / strongest) * 100).toFixed(0)}%` }}
              />
            </div>
            {row.estimated_traffic !== null && row.estimated_traffic !== undefined ? (
              <p
                className="ar-muted"
                style={{ margin: 'var(--ar-space-2) 0 0', fontSize: 'var(--ar-text-caption-size)' }}
              >
                Estimated {Math.round(row.estimated_traffic).toLocaleString('en-IN')} organic visits
                a month across their whole site — an estimate from the data service, not a
                measurement.
              </p>
            ) : null}
          </div>
        </section>
      ))}
    </div>
  )
}

/** A URL as its path, because every row on the screen is about the same site. */
function pathOf(url: string): string {
  try {
    const parsed = new URL(url)
    return `${parsed.pathname}${parsed.search}`
  } catch {
    return url
  }
}
